import type { PointerEvent as ReactPointerEvent } from "react";
import type { Pt } from "./cable-edge-geometry";

type SegmentHandler = (event: ReactPointerEvent<SVGLineElement>, target: SVGLineElement) => void;
const handlers = new WeakMap<SVGLineElement, SegmentHandler>();

export function registerCableSegment(target: SVGLineElement, handler: SegmentHandler): () => void {
  handlers.set(target, handler);
  return () => { handlers.delete(target); };
}

function distanceToSegment(point: Pt, line: SVGLineElement): number {
  const x = Number(line.getAttribute("x1"));
  const y = Number(line.getAttribute("y1"));
  const dx = Number(line.getAttribute("x2")) - x;
  const dy = Number(line.getAttribute("y2")) - y;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1,
    ((point.x - x) * dx + (point.y - y) * dy) / lengthSquared));
  return Math.hypot(point.x - x - t * dx, point.y - y - t * dy);
}

/** Wide hit strips make a lone wire easy to grab, but overlap adjacent lanes.
 * Resolve the nearest visible segment on press instead of letting SVG paint
 * order decide which wire moves. Coordinates are in the canvas's flow space. */
export function forwardToNearestCableSegment(
  event: ReactPointerEvent<SVGLineElement>,
  point: Pt,
  zoom: number,
): boolean {
  const target = event.currentTarget;
  const scope = target.closest(".react-flow__edges") ?? target.ownerSVGElement;
  if (!scope) return false;
  let nearest = target;
  let distance = distanceToSegment(point, target);
  // Only resolve an overlapping hit area. A synthetic press or a press whose
  // geometry moved before handling must not jump to an unrelated distant wire.
  if (distance * zoom > Number(target.getAttribute("stroke-width")) / 2 + 0.01) return false;
  for (const line of scope.querySelectorAll<SVGLineElement>("line[data-cable-segment]")) {
    if (line === target || !handlers.has(line)) continue;
    const candidate = distanceToSegment(point, line);
    // Retain the original target at exact crossings / identical routes.
    if (candidate < distance - 0.01) {
      nearest = line;
      distance = candidate;
    }
  }
  if (nearest === target) return false;
  handlers.get(nearest)!(event, nearest);
  return true;
}
